import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useQuote } from "./QuoteModal";
import { NAV_LINKS, TEL_URL, WA_URL } from "@/lib/site";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const openQuote = useQuote();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color,box-shadow] duration-300 ${
                scrolled
                    ? "border-slate-200 bg-white/95 shadow-[0_12px_40px_rgb(11,21,40,0.10)]"
                    : "border-slate-200/70 bg-white/85"
            }`}
        >
            <div
                className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-[padding] duration-300 sm:px-8 ${
                    scrolled ? "py-3" : "py-4"
                }`}
            >
                <Link to="/" aria-label="K1 Elevator's home" className="shrink-0">
                    <Logo testid="navbar-brand-logo" />
                </Link>

                <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={link.testid}
                            className={({ isActive }) =>
                                `relative text-sm font-semibold tracking-wide transition-colors duration-200 ${
                                    isActive
                                        ? "text-[#1D4ED8] after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-[#1D4ED8]"
                                        : "text-slate-600 hover:text-[#1D4ED8]"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 md:flex">
                    <a
                        href={TEL_URL}
                        data-testid="nav-call-btn"
                        className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-bold text-[#0B1528] transition-colors duration-200 hover:border-[#2563EB] hover:text-[#1D4ED8]"
                    >
                        <Phone className="h-4 w-4" />
                        +91 89694 49945
                    </a>
                    <button
                        type="button"
                        onClick={openQuote}
                        data-testid="nav-quote-cta-btn"
                        className="rounded-full bg-[#1D4ED8] px-5 py-2.5 text-sm font-bold text-white shadow-[0_4px_16px_rgba(37,99,235,0.35)] transition-[background-color,box-shadow,transform] duration-200 hover:bg-[#1E40AF] hover:shadow-[0_8px_24px_rgba(37,99,235,0.45)] active:scale-95"
                    >
                        GET A QUOTE
                    </button>
                </div>

                <button
                    type="button"
                    data-testid="mobile-menu-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 text-[#0B1528] transition-colors duration-200 hover:bg-slate-50 md:hidden"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu-panel"
                    className="border-t border-slate-200 bg-white/98 px-5 pb-6 pt-3 shadow-[0_24px_48px_rgb(11,21,40,0.12)] backdrop-blur-2xl md:hidden"
                >
                    <nav className="flex flex-col" aria-label="Mobile">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                data-testid={`mobile-${link.testid}`}
                                className={({ isActive }) =>
                                    `border-b border-slate-100 py-4 text-base font-semibold transition-colors duration-200 ${
                                        isActive ? "text-[#1D4ED8]" : "text-slate-700"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            openQuote();
                        }}
                        data-testid="mobile-quote-cta-btn"
                        className="mt-5 flex w-full items-center justify-center rounded-xl bg-[#1D4ED8] py-3.5 text-sm font-bold text-white"
                    >
                        GET A FREE QUOTE
                    </button>
                    <div className="mt-3 flex gap-3">
                        <a
                            href={TEL_URL}
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 py-3 text-sm font-bold text-[#0B1528]"
                        >
                            <Phone className="h-4 w-4" /> CALL
                        </a>
                        <a
                            href={WA_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#2563EB] py-3 text-sm font-bold text-white"
                        >
                            <MessageCircle className="h-4 w-4" /> WHATSAPP
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};
